import {
  mapImage,
  mapLink,
  mapText,
  mapTitle,
  normalizeSpacer,
  pickStr,
} from "@/components/DesignSystem/utils/teaserHelper"

/**
 * Maps a Two-Up Content (CXP) AEM container into KDS props.
 *
 * Picks the first child by `:itemsOrder` or first key in `:items`, then maps:
 * - `mod` (defaults to "white")
 * - `type` only when variant ∈ {"50-50","60-40","40-60"}
 * - `imageOrientation` only when ∈ {"landscape","portrait"}
 * - `heading`, `eyebrow` via `mapTitle`, `description` via `mapText`
 * - CTA buttons: `CTALink` (single link), `CTA` (first from `basic.cta`)
 * - `cards`: each requires `image` and `title`; includes optional `description`, `CTA`
 * - `topSpacer` / `bottomSpacer` via `normalizeSpacer`
 *
 * Returns `null` if no node is found. Omits keys with empty values.
 *
 * @param {Object} container AEM container node with `:itemsOrder` and `:items`.
 * @return {Object|null} Mapped props or `null` when nothing to map.
 */
export function mapTwoUpContentCXPProps(container) {
  const firstKey =
    container?.[":itemsOrder"]?.[0] ??
    Object.keys(container?.[":items"] ?? {})[0]

  const node = firstKey ? container?.[":items"]?.[firstKey] : undefined

  if (!node) return null

  const basic = node.basic
  const cardsRaw = node.card?.cards
  const mod = pickStr(basic?.mod) || "white"
  const heading = mapTitle(basic?.heading)
  const CTALink = mapLink(basic?.CTALink ?? basic?.ctaLink)
  const CTA = Array.isArray(basic?.cta)
    ? mapLink(basic.cta[0])
    : mapLink(basic?.cta)
  const eyebrow = mapTitle(basic?.eyebrow)
  const description = mapText(basic?.description)
  const type = (() => {
    const raw = pickStr(basic?.variant)
    const ok = new Set(["50-50", "60-40", "40-60"])
    return raw && ok.has(raw) ? raw : undefined
  })()
  const imageOrientation = (() => {
    const raw = pickStr(basic?.imageOrientation)
    const ok = new Set(["landscape", "portrait"])
    return raw && ok.has(raw) ? raw : undefined
  })()

  const topSpacer = basic?.topSpacer
    ? normalizeSpacer(basic.topSpacer.size ?? basic.topSpacer)
    : undefined
  const bottomSpacer = basic?.bottomSpacer
    ? normalizeSpacer(basic.bottomSpacer.size ?? basic.bottomSpacer)
    : undefined

  const cards = cardsRaw
    .map(c => {
      const image = mapImage(Array.isArray(c?.image) ? c.image[0] : c?.image)
      const title = mapTitle(Array.isArray(c?.title) ? c.title[0] : c?.title)
      const description = mapText(
        Array.isArray(c?.description) ? c.description[0] : c?.description
      )
      const CTA = mapLink(Array.isArray(c?.cta) ? c.cta[0] : c?.cta)
      if (!image || !title) return undefined
      return {
        image,
        title,
        ...(description ? { description } : {}),
        ...(CTA ? { CTA } : {}),
      }
    })
    .filter(Boolean)

  return {
    ...(mod ? { mod } : {}),
    ...(type ? { type } : {}),
    ...(imageOrientation ? { imageOrientation } : {}),
    ...(heading ? { heading } : {}),
    ...(eyebrow ? { eyebrow } : {}),
    ...(description ? { description } : {}),
    ...(CTALink ? { CTALink } : {}),
    ...(CTA ? { CTA } : {}),
    ...(cards.length ? { cards } : {}),
    ...(topSpacer ? { topSpacer } : {}),
    ...(bottomSpacer ? { bottomSpacer } : {}),
  }
}
